import type { IGeneratedRoute, IResourceConfig, IResourceLoadStats } from '@/Core/Contracts/UI/Resource'
import { resourceManager } from '@/Core/Managers/Resource'
import { IConfigLoader } from '../../Contracts/Loaders/IConfigLoader'
import { BaseLoader } from './BaseLoader'

export interface ResourceMap {
    [key: string]: IResourceConfig
}

/**
 * Typed environment variable helper
 */
interface EnvVars {
    VITE_FRAMEWORK_MODE?: 'true' | 'false'
    VITE_RESOURCE_AUTO_ROUTE?: 'true' | 'false'
    VITE_RESOURCE_VALIDATION?: 'strict' | 'loose' | 'none'
}

/**
 * Resource validation result
 */
interface ResourceValidationResult {
    valid: boolean
    errors: string[]
    warnings: string[]
}

export class ResourceKernelLoader extends BaseLoader {
    private resources: ResourceMap = {}
    private generatedRoutes: IGeneratedRoute[] = []
    private loadStats: IResourceLoadStats = {
        totalResources: 0,
        resourcesWithRoutes: 0,
        generatedRoutes: 0,
        errors: [],
    }
    private routeCache: Map<string, IGeneratedRoute[]> = new Map()
    private envVars: EnvVars
    private validationMode: 'strict' | 'loose' | 'none'

    constructor() {
        const config: IConfigLoader = {
            name: 'resources',
            priority: 5,
            clientSafe: true,
            dependencies: [],
        }
        super(config)
        
        // Load environment variables
        this.envVars = this.loadEnvVars()
        this.validationMode = this.envVars.VITE_RESOURCE_VALIDATION || 'loose'
    }

    private loadEnvVars(): EnvVars {
        return {
            VITE_FRAMEWORK_MODE: this.getEnvVar('VITE_FRAMEWORK_MODE') as 'true' | 'false',
            VITE_RESOURCE_AUTO_ROUTE: this.getEnvVar('VITE_RESOURCE_AUTO_ROUTE') as 'true' | 'false',
            VITE_RESOURCE_VALIDATION: this.getEnvVar('VITE_RESOURCE_VALIDATION') as 'strict' | 'loose' | 'none',
        }
    }

    async load(): Promise<ResourceMap> {
        try {
            this.log('Starting resource loading...')
            
            // Parallel loading with Promise.all
            await Promise.all([
                this.loadShipResources(),
                this.loadContainerResources(),
            ])
            
            await this.generateRoutes()

            // Validate resources if enabled
            if (this.validationMode !== 'none') {
                this.validateAllResources()
            }

            // Реєструємо всі ресурси в ResourceManager
            resourceManager.sets(this.resources)

            this.log(`Loaded ${this.loadStats.totalResources} resources with ${this.loadStats.generatedRoutes} routes`)
            return this.resources
        } catch (error) {
            this.log(`Error loading resources: ${error}`, 'error')
            return {}
        }
    }

    /**
     * Validate all loaded resources
     */
    private validateAllResources(): void {
        let validCount = 0
        let invalidCount = 0

        for (const [name, resource] of Object.entries(this.resources)) {
            const result = this.validateResource(name, resource)
            
            if (!result.valid) {
                invalidCount++
                if (this.validationMode === 'strict') {
                    this.log(`Validation failed for resource "${name}": ${result.errors.join(', ')}`, 'error')
                } else {
                    this.log(`Validation warning for resource "${name}": ${result.errors.join(', ')}`, 'warn')
                }
            } else {
                validCount++
            }

            // Log warnings even for valid resources
            if (result.warnings.length > 0) {
                this.log(`Warnings for resource "${name}": ${result.warnings.join(', ')}`, 'warn')
            }
        }

        this.log(`Resource validation complete: ${validCount} valid, ${invalidCount} invalid`)
    }

    /**
     * Validate a single resource
     */
    private validateResource(name: string, resource: IResourceConfig): ResourceValidationResult {
        const errors: string[] = []
        const warnings: string[] = []

        // Basic validation
        if (!name || typeof name !== 'string') {
            errors.push('Resource name is required')
        }

        if (!resource || typeof resource !== 'object') {
            errors.push('Resource config must be an object')
            return { valid: false, errors, warnings }
        }

        // Validate pageUrl if present
        if (resource.pageUrl !== undefined) {
            if (typeof resource.pageUrl !== 'string') {
                errors.push('pageUrl must be a string')
            } else if (!resource.pageUrl.startsWith('/')) {
                warnings.push('pageUrl should start with /')
            }
        }

        // Validate routeConfig
        if (resource.routeConfig !== undefined && typeof resource.routeConfig !== 'object') {
            errors.push('routeConfig must be an object')
        }

        // Validate detailConfig
        if (resource.detailConfig !== undefined) {
            if (typeof resource.detailConfig !== 'object') {
                errors.push('detailConfig must be an object')
            } else if (resource.detailConfig.mode && 
                       !['drawer', 'modal', 'page'].includes(resource.detailConfig.mode)) {
                warnings.push(`Unknown display mode: ${resource.detailConfig.mode}`)
            }
        }

        return {
            valid: errors.length === 0,
            errors,
            warnings,
        }
    }

    /**
     * Отримує всі завантажені ресурси
     */
    public getResources(): ResourceMap {
        return { ...this.resources }
    }

    /**
     * Отримує згенеровані роуты
     */
    public getGeneratedRoutes(): IGeneratedRoute[] {
        return [...this.generatedRoutes]
    }

    /**
     * Отримує статистику завантаження
     */
    public getLoadStats(): IResourceLoadStats {
        return { ...this.loadStats }
    }

    /**
     * Генерує роуты для всіх ресурсів з pageUrl
     */
    private async generateRoutes(): Promise<void> {
        this.generatedRoutes = []
        this.loadStats.generatedRoutes = 0
        this.loadStats.resourcesWithRoutes = 0

        // Skip route generation if disabled
        if (this.envVars.VITE_RESOURCE_AUTO_ROUTE === 'false') {
            this.log('Resource auto-routing is disabled')
            return
        }

        for (const [resourceName, resource] of Object.entries(this.resources)) {
            // Check cache first
            if (this.routeCache.has(resourceName)) {
                const cachedRoutes = this.routeCache.get(resourceName)!
                this.generatedRoutes.push(...cachedRoutes)
                this.loadStats.resourcesWithRoutes++
                this.loadStats.generatedRoutes += cachedRoutes.length
                continue
            }

            if (this.shouldGenerateRoutesForResource(resource)) {
                try {
                    const routes = this.generateResourceRoutes(resourceName, resource)
                    
                    // Cache the routes
                    this.routeCache.set(resourceName, routes)
                    
                    this.generatedRoutes.push(...routes)
                    this.loadStats.resourcesWithRoutes++
                    this.loadStats.generatedRoutes += routes.length
                } catch (error) {
                    const errorMsg = `Route generation error for ${resourceName}: ${error}`
                    this.loadStats.errors.push(errorMsg)
                    this.log(errorMsg, 'error')
                }
            }
        }
        
        this.log(`Generated ${this.loadStats.generatedRoutes} routes for ${this.loadStats.resourcesWithRoutes} resources`)
    }

    /**
     * Перевіряє, чи потрібно генерувати роуты для ресурсу
     */
    private shouldGenerateRoutesForResource(resource: any): resource is IResourceConfig {
        return (
            resource &&
            typeof resource === 'object' &&
            typeof resource.pageUrl === 'string' &&
            resource.pageUrl.trim() !== ''
        )
    }

    /**
     * Генерує роути для конкретного ресурсу
     * Генеруються тільки list та view роути
     * Create та edit відкриваються через drawer/modal (OpenResourceCardTask)
     */
    public generateResourceRoutes(resourceName: string, resource: IResourceConfig): IGeneratedRoute[] {
        const routes: IGeneratedRoute[] = []
        const baseUrl = resource.pageUrl || ''

        if (!baseUrl) {
            return routes
        }

        const routeConfig = resource.routeConfig || {
            list: true,
            view: true,
        }

        const defaultDisplayMode = resource.detailConfig?.mode || 'drawer'

        // Роут для списку
        if (routeConfig.list !== false) {
            routes.push({
                path: baseUrl,
                componentPath: this.generateComponentPath(resourceName, 'list'),
                resourceName,
                mode: 'list',
                displayMode: 'page',
            })
        }

        // Роут для перегляду
        if (routeConfig.view !== false) {
            routes.push({
                path: `${baseUrl}/:id`,
                componentPath: this.generateComponentPath(resourceName, 'view'),
                resourceName,
                mode: 'view',
                displayMode: defaultDisplayMode,
            })
        }


        return routes
    }

    /**
     * Генерує шлях до компоненту для роуту
     * Всі режими використовують UniversalResourcePage
     */
    private generateComponentPath(_resourceName: string, _mode: 'list' | 'view' | 'edit' | 'create'): string {
        // Один універсальний компонент для всіх режимів
        return 'Core/Components/Resource/UniversalResourcePage.tsx'
    }

    private capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }

    private async loadShipResources(): Promise<void> {
        if (!this.isClient()) return

        try {
            const shipResources = import.meta.glob('/src/Ship/Resources/*Resource.{js,jsx,ts,tsx}', { eager: true })

            for (const [key, module] of Object.entries(shipResources)) {
                const name = key
                    .split('/')
                    .pop()
                    ?.replace(/\.(js|jsx|ts|tsx)$/, '')

                if (!name || name.indexOf('Resource') === -1) continue

                const resourceName = name.replace('Resource', '')
                const moduleObj = module as any

                if (moduleObj[name]) {
                    moduleObj[name].key ??= resourceName
                    this.resources[resourceName] = moduleObj[name]
                    this.loadStats.totalResources++
                }
            }
        } catch (error) {
            const errorMsg = 'Не вдалося завантажити Ship ресурси: ' + error
            this.loadStats.errors.push(errorMsg)
            console.warn(errorMsg)
        }
    }

    private async loadContainerResources(): Promise<void> {
        if (!this.isClient()) return

        try {
            const containerResources = import.meta.glob(
                '/src/Containers/*Section/*Container/Resources/*Resource.{js,jsx,ts,tsx}',
                { eager: true }
            )

            for (const [key, module] of Object.entries(containerResources)) {
                const pathParts = key.split('/')

                if (pathParts[6] === undefined || pathParts[6].indexOf('Resource') === -1) {
                    continue
                }

                const sectionName = pathParts[3]?.replace('Section', '') || ''
                const containerName = pathParts[4]?.replace('Container', '') || ''
                const resourceName = pathParts[6].replace(/Resource\.(js|jsx|ts|tsx)$/, '')

                let namespace = []

                const removeSection = this.getEnvVar('VITE_RESOURCES_REMOVE_SECTION_FROM_PATH') !== 'true'
                const addContainer = this.getEnvVar('VITE_RESOURCES_ADD_CONTAINER_TO_PATH') === 'true'
                const duplicateResource = this.getEnvVar('VITE_RESOURCES_DUPLICATE_PATH_RESOURCE_ONLY') === 'true'

                if (removeSection) namespace.push(sectionName)
                if (addContainer) namespace.push(containerName)

                namespace.push(resourceName)
                const namespacePath = namespace.join('.')

                const resourceFullName = resourceName + 'Resource'
                const moduleObj = module as any

                if (moduleObj[resourceFullName]) {
                    moduleObj[resourceFullName].key ??= namespacePath
                    this.resources[namespacePath] = moduleObj[resourceFullName]
                    this.loadStats.totalResources++

                    if (duplicateResource) {
                        this.resources[resourceName] = moduleObj[resourceFullName]
                        this.loadStats.totalResources++
                    }
                }
            }
        } catch (error) {
            const errorMsg = 'Не вдалося завантажити Container ресурси: ' + error
            this.loadStats.errors.push(errorMsg)
            console.warn(errorMsg)
        }
    }
}
